<template>
  <v-list density="comfortable" :prepend-gap="10">
    <side-menu-item v-for="menuItem in primaryMenuList" :key="menuItem.title" :rail="rail" :menu-item="menuItem" />

    <side-menu-item v-if="hasSharedAssets" :rail="rail" :menu-item="sharedMenuItem" />

    <v-divider class="my-2" />

    <side-menu-item v-for="menuItem in secondaryMenuList" :key="menuItem.title" :rail="rail" :menu-item="menuItem" />
  </v-list>
</template>

<script setup lang="ts">
import type { MenuItem } from "./SideMenuItem.vue"

defineProps<{ rail?: boolean }>()

const { t } = useI18n()

const assetCategoryStore = useAssetCategoryStore()
const sharedStore = useSharedStore()
const authStore = useAuthStore()
const contractStore = useContractStore()
const supplierStore = useSupplierStore()
const costSpecificationStore = useCostSpecificationStore()

const { isSuperAdmin, currentOrganization } = storeToRefs(authStore)
const { isLoggedInOnSupplier, isSupplierPlanner, isSupplierManager } = storeToRefs(supplierStore)

const { hasOrganizationWithScope } = authStore

const { data: assetCategories } = useLazyAsyncData(
  `used-categories:${currentOrganization.value}`,
  async () => {
    return isLoggedInOnSupplier.value || !currentOrganization.value ? [] : await assetCategoryStore.getUsedCategories()
  },
  {
    watch: [currentOrganization],
    default: () => [] as Array<AssetCategory>,
    immediate: true,
  },
)

const { data: hasSharedAssets } = useLazyAsyncData("has-shared-assets", () => sharedStore.hasSharedAssets(), { default: () => false })

const { refreshSummaryKey: constSpecificationRefreshSummaryKey } = storeToRefs(costSpecificationStore)
const { refreshSummaryKey: contractRefreshSummaryKey } = storeToRefs(contractStore)

const { data: costSpecificationSummary } = useLazyAsyncData(
  `cost-specification-summary:${currentOrganization.value}:${constSpecificationRefreshSummaryKey.value}`,
  async () => {
    if (!currentOrganization.value || isLoggedInOnSupplier.value) {
      return {
        hasSpecifications: false,
        hasSpecificationsAwaitingApproval: false,
        specificationsAwaitingApprovalCount: 0,
      }
    }

    return costSpecificationStore.getSpecificationSummary(currentOrganization.value)
  },
  {
    watch: [currentOrganization, isLoggedInOnSupplier, constSpecificationRefreshSummaryKey],
    default: () => ({
      hasSpecifications: false,
      hasSpecificationsAwaitingApproval: false,
      specificationsAwaitingApprovalCount: 0,
    }),
    immediate: true,
  },
)
const { data: contractsSummary } = useLazyAsyncData(
  `contracts-summary:${currentOrganization.value}:${contractRefreshSummaryKey.value}`,
  async () => {
    if (!currentOrganization.value) {
      return {
        pendingContracts: 0,
      }
    }

    return contractStore.getSummary()
  },
  {
    watch: [currentOrganization, isLoggedInOnSupplier, contractRefreshSummaryKey],
    default: () => ({
      pendingContracts: 0,
    }),
    immediate: true,
  },
)

const showSupplierAdmin = computed(() => {
  // Check if supplier is logged in to his own supplier and has the supplier module enabled
  if (!isLoggedInOnSupplier.value) {
    return false
  }

  // Check if supplier user is manager or planner
  return isSuperAdmin.value || isSupplierPlanner.value || isSupplierManager.value
})

const organizationHasSpecification = computed(() => {
  return costSpecificationSummary.value?.hasSpecifications ?? false
})

const hasSpecificationsAwaitingApproval = computed(() => {
  return costSpecificationSummary.value?.hasSpecificationsAwaitingApproval ?? false
})

const primaryMenuList = computed<Array<MenuItem>>(() => [
  {
    icon: "home",
    title: "dashboard.title",
    to: "/dashboard",
  },
  {
    icon: "$asset",
    title: "assets.title",
    to: `/assets?tab=LIST&filters=${JSON.stringify({ "category.description": [] })}`,
    activeMethod: () => isAssetUrlActive([]),
    ...(!isLoggedInOnSupplier.value && {
      children: assetCategories.value!.map((category) => ({
        iconLabel: category.marker.description,
        color: category.marker.color,
        title: category.description,
        roundedAvatar: true,
        to: `/assets?tab=LIST&filters=${JSON.stringify({ "category.description": [category.description] })}`,
        activeMethod: () => isAssetUrlActive([category.description]),
      })),
    }),
  },
  {
    icon: "$ticket",
    title: "tickets.title",
    to: `/tickets?filters=${JSON.stringify({ type: [] })}`,
    disabled: [
      hasOrganizationWithScope(AuthScope.CAN_VIEW_INSPECTIONS),
      hasOrganizationWithScope(AuthScope.CAN_VIEW_MALFUNCTIONS),
      hasOrganizationWithScope(AuthScope.CAN_VIEW_ACTIONS),
    ].every((hasAccess) => !hasAccess),
    activeMethod: () => isTicketUrlActive([]),
    children: [
      {
        icon: "$inspection",
        color: "inspection",
        title: "inspections.title",
        to: `/tickets?filters=${JSON.stringify({ type: ["Inspection", "NEN3140", "NEN1010"] })}`,
        disabled: !hasOrganizationWithScope(AuthScope.CAN_VIEW_INSPECTIONS),
        activeMethod: () => isTicketUrlActive(["Inspection", "NEN3140", "NEN1010"]),
      },
      {
        icon: "$malfunction",
        color: "malfunction",
        title: "malfunctions.title",
        to: `/tickets?filters=${JSON.stringify({ type: ["Malfunction"] })}`,
        disabled: !hasOrganizationWithScope(AuthScope.CAN_VIEW_MALFUNCTIONS),
        activeMethod: () => isTicketUrlActive(["Malfunction"]),
      },
      {
        icon: "$action",
        color: "action",
        title: "actions.title",
        to: `/tickets?filters=${JSON.stringify({ type: ["Action"] })}`,
        disabled: !hasOrganizationWithScope(AuthScope.CAN_VIEW_ACTIONS),
        activeMethod: () => isTicketUrlActive(["Action"]),
      },
    ],
  },
  {
    icon: "$finance",
    title: "finance.title",
    to: "/finance",
    hideDropdownTitle: true,
    children: [
      {
        title: "finance.myp.title",
        to: "/finance/myp",
        disabled: !hasOrganizationWithScope(AuthScope.CAN_VIEW_FINANCES_BUDGET_OVERVIEW),
      },
      {
        title: "finance.tickets.title",
        to: "/finance/tickets",
        disabled: !hasOrganizationWithScope(AuthScope.CAN_VIEW_FINANCES_COSTS_AND_LABOUR_OVERVIEW),
      },
      {
        title: "finance.contracts.title",
        to: "/finance/contracts",
        ...(contractsSummary.value.pendingContracts && {
          badge: {
            badgeColor: "error",
            itemCount: contractsSummary.value.pendingContracts ?? 0,
            text: t("contracts.hasPending", contractsSummary.value.pendingContracts ?? 0),
          },
        }),
      },
      ...(showSupplierAdmin.value || organizationHasSpecification.value
        ? [
            {
              title: "finance.costSpecifications.title",
              to: "/finance/cost-specifications",
              ...(hasSpecificationsAwaitingApproval.value && {
                badge: {
                  badgeColor: "error",
                  itemCount: costSpecificationSummary.value?.specificationsAwaitingApprovalCount ?? 0,
                  text: t("shared.itemsCount", { count: costSpecificationSummary.value?.specificationsAwaitingApprovalCount ?? 0 }),
                },
              }),
            },
          ]
        : []),

      ...(showSupplierAdmin.value
        ? [
            {
              title: "internalPriceList.title",
              to: "/finance/supplier-price-lists",
            },
          ]
        : []),
    ],
  },
  {
    icon: "$analytics",
    title: "analytics.title",
    to: "/analytics",
    hideDropdownTitle: true,
    children: [
      {
        title: "risk.analysis",
        to: "/analytics/risk",
        disabled: !hasOrganizationWithScope(AuthScope.CAN_VIEW_ANALYSIS_RISK_OVERVIEW),
      },
      {
        title: "inspections.analysis",
        to: "/analytics/inspections",
        disabled: !hasOrganizationWithScope(AuthScope.CAN_VIEW_ANALYSIS_INSPECTION_OVERVIEW),
      },
      {
        title: "analytics.dataQuality.title",
        to: "/analytics/data-quality",
      },
    ],
  },
  {
    icon: "$overview",
    title: "overviews.title",
    to: "/overviews",
  },
  ...(isLoggedInOnSupplier.value
    ? [
        {
          icon: "$organization",
          title: "organizations",
          to: "/organizations",
        },
      ]
    : []),
])

const sharedMenuItem: MenuItem = {
  icon: "$shared",
  title: "shared.title",
  to: "/shared",
  hideDropdownTitle: true,
  children: [
    {
      title: "shared.assets.title",
      to: "/shared/assets",
    },
    {
      title: "shared.tickets.title",
      to: "/shared/tickets",
    },
  ],
}

const secondaryMenuList = computed<Array<MenuItem>>(() => [
  {
    icon: "$support",
    title: "support.title",
    to: "/support",
  },
  {
    icon: "$settings",
    title: "settings.title",
    to: "/settings",
  },
  ...(hasOrganizationWithScope(AuthScope.CAN_VIEW_CHANGELOGS_OVERVIEW)
    ? [
        {
          icon: "$mutationLogs",
          title: "mutationLog.title",
          to: "/mutation-log",
        },
      ]
    : []),
])

const isTicketUrlActive = (ticketTypes: Array<string>) => {
  const route = useRoute()
  if (!route.path.startsWith("/tickets")) return false

  try {
    const filters = route.query.filters ? JSON.parse(route.query.filters as string) : {}
    const activeFilter = filters["type"]

    if (ticketTypes.length === 0) {
      return !activeFilter || (Array.isArray(activeFilter) && activeFilter.length === 0)
    }

    return Array.isArray(activeFilter) && ticketTypes.some((type) => activeFilter.includes(type))
  } catch {
    return false
  }
}

const isAssetUrlActive = (categories: Array<string>) => {
  const route = useRoute()
  if (!route.path.startsWith("/assets")) return false

  try {
    const filters = route.query.filters ? JSON.parse(route.query.filters as string) : {}
    const activeFilter = filters["category.description"]

    if (categories.length === 0) {
      return !activeFilter || (Array.isArray(activeFilter) && activeFilter.length === 0)
    }

    return Array.isArray(activeFilter) && categories.some((cat) => activeFilter.includes(cat))
  } catch {
    return false
  }
}
</script>
